import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

// Native (Android/iOS via Capacitor) push registration. On native platforms the
// web FCM path doesn't apply — the OS delivers pushes via the Capacitor plugin,
// so we register here and persist the native FCM token using the exact same
// Firestore contract as the web path: users/{uid}/fcmTokens/{token} plus the
// mirrored fcmToken field on the user doc that iOS reads.

export function isNativePlatform() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

let listenersAttached = false;
let pendingUser = null;
let pendingDb = null;

async function saveToken(user, db, token) {
  const platform = Capacitor.getPlatform(); // 'android' | 'ios'
  await setDoc(doc(db, 'users', user.uid, 'fcmTokens', token), {
    token,
    platform,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    userAgent: navigator.userAgent
  }, { merge: true });
  await setDoc(doc(db, 'users', user.uid), { fcmToken: token }, { merge: true });
  return token;
}

function attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  PushNotifications.addListener('registration', (token) => {
    if (pendingUser && pendingDb) {
      saveToken(pendingUser, pendingDb, token.value).catch((e) =>
        console.error('Failed to save native FCM token:', e)
      );
    }
  });

  PushNotifications.addListener('registrationError', (error) => {
    console.error('Native push registration failed:', error);
  });

  // Foreground push — dispatch a DOM event so portals can react (badge counts,
  // banners) the same way they do for web onMessage payloads.
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    window.dispatchEvent(new CustomEvent('native-push-received', { detail: notification }));
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    window.dispatchEvent(new CustomEvent('native-push-action', { detail: action }));
  });
}

// Mirrors requestNotificationPermissionAndSaveToken for native builds.
// Returns the FCM token, or null when registration can't complete yet (the
// 'registration' listener will save it later once it arrives).
export async function registerForNativePush(user, db) {
  if (!user || !db) return null;
  try {
    attachListeners();
    pendingUser = user;
    pendingDb = db;

    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.warn('Native push permission not granted:', permStatus.receive);
      return null;
    }

    await PushNotifications.register();

    // getDeliveredNotifications is not needed here; the token arrives via the
    // 'registration' listener. Nothing further to return synchronously.
    return null;
  } catch (e) {
    console.error('registerForNativePush failed:', e);
    return null;
  }
}
